import { tacticKey, type PostureCounter, type PostureTopEntry, type SignalAssignStatus, type WorkbookLockCheck } from '@bloo-team/api-client'
import type { Signal } from './signal-model'

/**
 * Signals queue — the pure rules behind the split-pane page header: queue
 * tabs, the stats-band change line, the ATT&CK heat strip, the stage filter,
 * stream health and the open-workbook lock refusal (docs/signals.md).
 *
 * Every rule here was shaped by the 2026-10-05 capture
 * (docs/inventory/signals-queue-capture.md); the comments name the finding.
 */

/* ------------------------------------------------------------- queue tabs */

export const QUEUE_TABS = ['unassigned', 'all', 'fp'] as const
export type QueueTab = (typeof QUEUE_TABS)[number]

export const QUEUE_TAB_LABEL: Record<QueueTab, string> = {
  unassigned: 'Unassigned',
  all: 'All',
  fp: 'False positives',
}

/**
 * The queue chips a lens offers. False positives is a server filter
 * (`falsepositive = true`) the Storylines lens cannot apply — its subgraphs
 * come from the connected-signals job — so that lens does not offer it.
 */
export const queueTabsFor = (lens: string): readonly QueueTab[] => (lens === 'storylines' ? QUEUE_TABS.filter((q) => q !== 'fp') : QUEUE_TABS)

export const isQueueTab = (v: unknown): v is QueueTab => typeof v === 'string' && (QUEUE_TABS as readonly string[]).includes(v)

/**
 * The server's queue split. Signals carry no assignee: `unassigned` = a
 * signal in NO case, not flagged false positive and with no remark (the
 * server's `assigned` is the complement). All reads the whole window;
 * False positives too, with `falsepositive = true` on top (SignalFilter
 * `fpOnly`), so a mistaken flag can be found and unmarked.
 */
export function assignStatusFor(tab: QueueTab): SignalAssignStatus | undefined {
  return tab === 'unassigned' ? 'unassigned' : undefined
}

/* ------------------------------------------------------- stats-band MTTD */

const MTTD_UNITS: readonly [string, number][] = [
  ['d', 86_400],
  ['h', 3_600],
  ['m', 60],
  ['s', 1],
]

/**
 * MTTD seconds as at most three units, from the largest non-zero one down,
 * zeros after it kept: `1d 1h 1m` · `2h 0m 0s` · `2m 5s` · `45s`; under a
 * second, `0s`. Whole seconds (floored).
 */
export function mttdLabel(seconds: number): string {
  let left = Math.max(0, Math.floor(seconds))
  const parts = MTTD_UNITS.map(([u, size]) => {
    const n = Math.floor(left / size)
    left -= n * size
    return { u, n }
  })
  const start = parts.findIndex((x) => x.n > 0)
  if (start < 0) return '0s'
  return parts
    .slice(start, start + 3)
    .map((x) => `${x.n}${x.u}`)
    .join(' ')
}

/* ------------------------------------------------------- stats-band delta */

export type DeltaView =
  | { kind: 'none' }
  | { kind: 'new' }
  | { kind: 'pct'; dir: 'up' | 'down' | 'flat'; pct: number }

/**
 * The stats-band change line. `cases/stats` sends `percent_diff = new × 100`
 * when the previous period was zero (captured: new 16 → 1600), which reads as
 * a 1600% jump; that case shows as "new · prior 0" instead.
 */
export function deltaView(c: PostureCounter | null | undefined): DeltaView {
  if (!c || c.percentDiff === null || !Number.isFinite(c.percentDiff)) return { kind: 'none' }
  if (c.count > 0 && c.percentDiff === c.count * 100) return { kind: 'new' }
  const pct = c.percentDiff
  return { kind: 'pct', dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat', pct: Math.abs(pct) }
}

/** `12%`, `2.5%` — one decimal under 10. */
export function pctLabel(pct: number): string {
  return `${pct >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10}%`
}

/* ------------------------------------------------------------- heat strip */

export interface TacticCell {
  /** Display name, from the matrix skeleton. */
  tactic: string
  count: number
}

/**
 * One cell per skeleton tactic (attack/matrix, `Unclassified` dropped), with
 * its count from `cases/stats` `top_tactics` — the server's top 10 for the
 * window, whole scope (not the tab); a tactic outside the top 10 reads 0
 * (docs/signals.md §ATT&CK heat strip). Names fold through tacticKey (case +
 * "Defence"). When the skeleton is not loaded yet, the top tactics stand in.
 */
export function tacticCells(top: readonly PostureTopEntry[] | undefined, skeleton: readonly string[] | undefined): TacticCell[] {
  const counts = new Map<string, number>()
  const seen = new Map<string, string>()
  for (const t of top ?? []) {
    if (!t.name) continue
    const k = tacticKey(t.name)
    counts.set(k, (counts.get(k) ?? 0) + t.count)
    if (!seen.has(k)) seen.set(k, t.name)
  }
  const names = skeleton?.length
    ? skeleton.filter((t) => tacticKey(t) !== 'unclassified')
    : [...seen.values()].sort((a, b) => a.localeCompare(b))
  return names.map((tactic) => ({ tactic, count: counts.get(tacticKey(tactic)) ?? 0 }))
}

/** Is `tactic` among the filter's picks, folding names the way the strip does. */
export function tacticPicked(picks: readonly string[], tactic: string): boolean {
  const k = tacticKey(tactic)
  return picks.some((p) => tacticKey(p) === k)
}

/** Toggle one strip cell in the Tactic filter, replacing any dirty spelling of it. */
export function toggleTactic(picks: readonly string[], tactic: string, present: readonly string[]): string[] {
  const k = tacticKey(tactic)
  if (picks.some((p) => tacticKey(p) === k)) return picks.filter((p) => tacticKey(p) !== k)
  // The filter matches exact signal tactics, so add every spelling present.
  const spellings = present.filter((t) => tacticKey(t) === k)
  return [...picks, ...(spellings.length ? spellings : [tactic])]
}

/* ----------------------------------------------------------- stage filter */

/** Picker label for a signal with no `$Stage`. */
export const STAGE_NONE = 'none'

export function stageOf(s: Pick<Signal, 'stage'>): string {
  return s.stage ?? STAGE_NONE
}

/** Stage filter: empty = any; `none` matches only rows without a stage. Client-side (the server rejects `Stage`). */
export function matchesStage(picks: readonly string[], s: Pick<Signal, 'stage'>): boolean {
  return !picks.length || picks.includes(stageOf(s))
}

/** Options: the workbook/meta list, then any stage the rows carry that the list lacks, then `none`. */
export function stageOptions(meta: readonly string[] | undefined, signals: readonly Signal[]): string[] {
  const out = [...(meta ?? [])]
  for (const s of signals) if (s.stage && !out.includes(s.stage)) out.push(s.stage)
  out.push(STAGE_NONE)
  return out
}

/* ---------------------------------------------------------- stream health */

/**
 * Before this local hour every stream reads 0 "today", so silent is muted —
 * the same rule as the Fabric overview (re-implemented: products never import
 * each other). Browser-local until a capture says which clock starts "today".
 */
export const SILENT_MUTE_HOUR = 6

export function silentMuted(now: number): boolean {
  return new Date(now).getHours() < SILENT_MUTE_HOUR
}

/**
 * Streams with no events so far today (`daily-stream-details` is a running
 * count, no history — so there is no "low" or trend). A stream the loaded
 * signals name but the daily read lacks counts as 0. Empty while muted or
 * while the read is missing.
 */
export function silentStreams(
  counts: Record<string, number> | undefined,
  signalStreams: readonly string[],
  now: number,
): string[] {
  if (!counts || silentMuted(now)) return []
  const names = new Set([...Object.keys(counts), ...signalStreams])
  return [...names].filter((n) => (counts[n] ?? 0) === 0).sort((a, b) => a.localeCompare(b))
}

/* ------------------------------------------------------------- lock check */

/**
 * Why Open workbook refuses, or null to navigate. Refuses when the check did
 * not succeed, when the workbook is private, or when the reply carries a
 * message (captured: "Workbook does not exist!" with status success). A
 * locked workbook's reply was never observed — UNVERIFIED.
 */
export function lockRefusal(check: WorkbookLockCheck): string | null {
  if (check.status !== 'success') return check.message || 'The workbook lock check failed.'
  if (check.isPrivate === true) return check.message || 'This workbook is private or locked.'
  if (check.message) return check.message
  return null
}
